import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Divider, Alert, Button, Space, Typography } from '@/shared/ui/primitives';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import RoomAdForm from '../components/RoomAdForm';
import UserSelector from '../components/UserSelector';
import AdImageManager from '../components/AdImageManager';
import { useCreateRoomAd } from '../hooks/useRoomAdMutations';

/**
 * Admin Create Ad flow:
 *   Step 1 — fill form (owner, title, location, price, flags) → submit creates the ad
 *   Step 2 — with the created ad id, show the image manager so admin can upload images
 *   Step 3 — admin clicks Done → navigate to the list, or Edit more → opens the full edit page
 *
 * This avoids the broken "can't upload until you save first" state.
 */
export default function RoomAdCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userId, setUserId] = useState<number | null>(null);
  const [userError, setUserError] = useState('');
  const [createdAdId, setCreatedAdId] = useState<number | null>(null);
  const [createdAdTitle, setCreatedAdTitle] = useState<string>('');
  const mutation = useCreateRoomAd();

  const handleUserChange = useCallback((newUserId: number | null) => {
    setUserId(newUserId);
    setUserError('');
  }, []);

  const handleSubmit = (formData: any) => {
    if (!userId) {
      setUserError(t('roomAds.userRequired'));
      return;
    }
    mutation.mutate({ userId, ...formData }, {
      onSuccess: (data: any) => {
        setCreatedAdId(data.id);
        setCreatedAdTitle(data.title || '');
      },
    });
  };

  if (createdAdId) {
    return (
      <div>
        <PageHeader back
          title={t('roomAds.createStep2Title')}
          subtitle={t('roomAds.createStep2Subtitle', { id: createdAdId, title: createdAdTitle })}
          action={
            <Space>
              <Button onClick={() => navigate(`/room-ads/${createdAdId}/edit`)}>{t('roomAds.editMore')}</Button>
              <Button type="primary" onClick={() => navigate('/room-ads')}>{t('roomAds.done')}</Button>
            </Space>
          }
        />
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('roomAds.images.createSuccessTitle', { id: createdAdId })}
          description={t('roomAds.images.createSuccessHint')}
        />
        <Card>
          <Typography.Title level={5} style={{ marginTop: 0 }}>{t('roomAds.images.sectionTitle')}</Typography.Title>
          <Divider style={{ margin: '8px 0 16px' }} />
          <AdImageManager adId={createdAdId} />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader back title={t('roomAds.createTitle')} subtitle={t('roomAds.createSubtitle')} />
      <Card>
        <UserSelector value={userId} onChange={handleUserChange} error={userError} required />
        <Divider />
        <RoomAdForm onSubmit={handleSubmit} isLoading={mutation.isPending} isCreate />
      </Card>
    </div>
  );
}
